import clsx from 'clsx';
import TooltipCustom from 'core/modules/atoms/Tooltip';
import React from 'react';
import Icon from '../Icon/Icon';
import type { IconName } from '../../icons/types';

export type TagColor =
  | 'default'
  | 'primary'
  | 'success'
  | 'blue'
  | 'transparent'
  | 'brand-solid'
  | 'error'
  | 'pastel-red'
  | 'pastel-yellow'
  | 'pastel-blue'
  | 'pastel-grey'
  | 'pastel-green'
  | 'pastel-purple'
  | 'pastel-pink'
  | 'pastel-orange'
  | 'pastel-comp-yellow'
  | 'pastel-darkgreen'
  | 'pastel-comp-green'
  | 'pastel-comp-blue'
  | 'pastel-darkblue'
  | 'pastel-white';

type TagSize = 'sm' | 'md' | 'lg';
type TagShape = 'pill' | 'rounded';

export type TagProps = {
  ariaLabel?: string;
  className?: string;
  color?: TagColor;
  customBackgroundColor?: string;
  customTextColor?: string;
  hasBorder?: boolean;
  isFullWidth?: boolean;
  label?: string | React.ReactNode;
  leftIcon?: IconName;
  onClick?: () => void;
  rightIcon?: IconName;
  shape?: TagShape;
  shouldTruncate?: boolean;
  size?: TagSize;
  // ReactElement (no ReactNode): TooltipCustom.title es PropTypes.node ->
  // NonNullable<ReactNodeLike>, y ReactNode arrastra boolean/null que no encaja.
  tooltip?: string | React.ReactElement;
  tooltipClassName?: string;
};

function IconSlot({ name }: Readonly<{ name?: IconName }>): JSX.Element | null {
  if (!name) return null;
  return (
    <span aria-hidden="true" className="tag__icon">
      <Icon color="inherit" name={name} />
    </span>
  );
}

function buildInlineStyle(
  backgroundColor: string | undefined,
  textColor: string | undefined,
): React.CSSProperties | undefined {
  if (!backgroundColor && !textColor) return undefined;
  const styles: Record<string, string> = {};
  if (backgroundColor) {
    styles.background = backgroundColor;
    styles['--tag-bg'] = backgroundColor;
  }
  if (textColor) {
    styles.color = textColor;
    styles['--tag-text-color'] = textColor;
  }
  return styles as React.CSSProperties;
}

function warnIfMissingAccessibleName(
  ariaLabel: string | undefined,
  hasIcon: boolean,
  hasLabel: boolean,
  isInteractive: boolean,
): void {
  if (process.env.NODE_ENV === 'production' || hasLabel || ariaLabel) return;
  if (isInteractive) {
    console.warn('[Tag] Interactive Tag (onClick set) has no accessible name. Provide a non-empty label or ariaLabel.');
    return;
  }
  if (hasIcon) {
    console.warn('[Tag] Icon-only Tag has no accessible name. Provide a non-empty label or ariaLabel.');
  }
}

function Tag({
  ariaLabel,
  className,
  color = 'default',
  customBackgroundColor,
  customTextColor,
  hasBorder,
  isFullWidth = false,
  label,
  leftIcon,
  onClick,
  rightIcon,
  shape = 'pill',
  shouldTruncate = false,
  size = 'md',
  tooltip,
  tooltipClassName,
}: Readonly<TagProps>): JSX.Element {
  const hasOnClick = typeof onClick === 'function';
  const isFocusableForTooltip = Boolean(tooltip) && !hasOnClick;
  const shouldRenderAsButton = hasOnClick || isFocusableForTooltip;
  const hasLabel = label !== undefined && label !== null && label !== '';
  const hasIcon = Boolean(leftIcon) || Boolean(rightIcon);
  const isIconOnly = !hasLabel && hasIcon;

  warnIfMissingAccessibleName(ariaLabel, hasIcon, hasLabel, hasOnClick);

  const rootClassName = clsx(
    'tag',
    `tag--color-${color}`,
    `tag--size-${size}`,
    `tag--shape-${shape}`,
    {
      'tag--clickable': hasOnClick,
      'tag--full-width': isFullWidth,
      'tag--truncate': shouldTruncate,
      'tag--has-border': hasBorder === true,
      'tag--no-border': hasBorder === false,
      'tag--pastel': color.startsWith('pastel-'),
      'tag--icon-only': isIconOnly,
    },
    className,
  );

  const inlineStyle = buildInlineStyle(customBackgroundColor, customTextColor);
  const iconOnlyRole = !shouldRenderAsButton && isIconOnly && ariaLabel ? 'img' : undefined;

  const content = (
    <>
      <IconSlot name={leftIcon} />
      {hasLabel && <span className="tag__label">{label}</span>}
      <IconSlot name={rightIcon} />
    </>
  );

  const tagElement = shouldRenderAsButton ? (
    <button aria-label={ariaLabel} className={rootClassName} onClick={onClick} style={inlineStyle} type="button">
      {content}
    </button>
  ) : (
    /* aria-label is prohibited on a role-less element with no text content, so the
       icon-only variant needs role="img" for the name to reach screen readers. */
    <div aria-label={ariaLabel} className={rootClassName} role={iconOnlyRole} style={inlineStyle}>
      {content}
    </div>
  );

  if (!tooltip) return tagElement;

  return (
    <TooltipCustom
      classes={{ tooltip: clsx('tag-tooltip', tooltipClassName), popper: 'tag-tooltip__popper' }}
      placement="top"
      title={tooltip}
    >
      {tagElement}
    </TooltipCustom>
  );
}

export default Tag;
